import Phaser from 'phaser';

/**
 * 键盘焦点导航 + 箭头指示器（所有菜单通用）
 *
 * 用法：
 *   const nav = new FocusNav(scene, scrollFactor, depth);
 *   nav.setItems([{ row, col, target, onSelect, onFocus?, onBlur? }, ...]);
 *   // 方向键 → nav.move(dx, dy)；回车 → nav.select()
 *
 * 每个 item 有 (row, col) 网格位置：
 *   - 上下键：在不同 row 之间移动（同列优先）
 *   - 左右键：在同一 row 内左右移动
 * 箭头（▶）自动定位到当前焦点元素左侧并闪烁；聚焦/失焦时可选高亮。
 */
export interface FocusItem {
  row: number;
  col: number;
  /** 用于定位箭头与（可选）高亮的对象 */
  target: Phaser.GameObjects.GameObject & { x: number; y: number; displayWidth?: number };
  onSelect: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const ARROW_GAP = 20;

export class FocusNav {
  private items: FocusItem[] = [];
  private index = 0;
  private arrow: Phaser.GameObjects.Text;
  private bob: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene, scrollFactor = 1, depth = 300) {
    this.arrow = scene.add
      .text(0, 0, '▶', { fontSize: '22px', fontFamily: 'monospace', color: '#f1c40f' })
      .setOrigin(0.5)
      .setScrollFactor(scrollFactor)
      .setDepth(depth)
      .setVisible(false);

    this.bob = scene.tweens.add({
      targets: this.arrow,
      alpha: 0.4,
      duration: 450,
      yoyo: true,
      repeat: -1,
    });
  }

  get focused(): FocusItem | undefined {
    return this.items[this.index];
  }

  setItems(items: FocusItem[], startIndex = 0): void {
    this.items = items;
    if (this.items.length === 0) {
      this.arrow.setVisible(false);
      return;
    }
    this.index = Phaser.Math.Clamp(startIndex, 0, this.items.length - 1);
    this.arrow.setVisible(true);
    this.items[this.index].onFocus?.();
    this.positionArrow();
  }

  /** dx / dy 为方向（-1 / 0 / 1） */
  move(dx: number, dy: number): void {
    if (this.items.length === 0) return;
    const cur = this.items[this.index];
    let best = -1;
    let bestScore = Infinity;

    this.items.forEach((it, i) => {
      if (i === this.index) return;
      let score = Infinity;
      if (dx !== 0) {
        if (it.row !== cur.row) return; // 左右：同一行内
        const dc = it.col - cur.col;
        if (dx > 0 ? dc <= 0 : dc >= 0) return;
        score = Math.abs(dc);
      } else {
        const dr = it.row - cur.row;
        if (dy > 0 ? dr <= 0 : dr >= 0) return;
        score = Math.abs(dr) * 100 + Math.abs(it.col - cur.col); // 行优先，再看列
      }
      if (score < bestScore) {
        bestScore = score;
        best = i;
      }
    });

    if (best >= 0) {
      cur.onBlur?.();
      this.index = best;
      this.items[this.index].onFocus?.();
      this.positionArrow();
    }
  }

  select(): void {
    this.focused?.onSelect();
  }

  private positionArrow(): void {
    const t = this.items[this.index].target as any;
    const w = t.displayWidth ?? 0;
    this.arrow.setPosition(t.x - w / 2 - ARROW_GAP, t.y);
  }

  destroy(): void {
    this.bob?.remove();
    this.arrow?.destroy();
    this.items = [];
  }
}
